import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { can } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ui-system";
import { apiErrorMessage, EmailPreviewHeader } from "@/components/EmailTemplateRecipients";
import {
  configPayload, listsFromSaved, newIdempotencyKey, recipientsChanged, sendBlocker, sendPayload,
} from "@/lib/clientAnnouncement";
import { toast } from "sonner";
import { Megaphone, Send, Loader2, TriangleAlert } from "lucide-react";
import RecipientFields from "@/components/client-announcement/RecipientFields";
import MessageEditor from "@/components/client-announcement/MessageEditor";
import LocationsUpload from "@/components/client-announcement/LocationsUpload";
import EmailPreviewFrame from "@/components/client-announcement/EmailPreviewFrame";
import useAnnouncementPreview from "@/components/client-announcement/useAnnouncementPreview";

/**
 * Common Client Notification — an operational email to clients.
 *
 * The announcement type only picks the default subject / message / closing (all
 * editable; each type keeps its own unsent edits while you switch). The optional
 * affected-locations upload applies to every type. Recipients (To / CC / BCC) start
 * from the saved lists — the notification_configs doc for this action type, saved
 * with Save Recipients through the normal notification-config endpoints like the
 * other templates (greeting names in recipient_names; at least one To to save) — and
 * can be edited per send; a send carries the lists shown, saved or not. The preview is the backend's own HTML
 * (POST …/client-announcement/preview), so it matches the delivered email. Trigger
 * Now posts to …/send, which queues ONE email per To address (CC/BCC copied on each;
 * BCC on the SMTP envelope only) as a background send; the page polls …/jobs/{id}
 * and reports only what the backend confirms
 * (backend/services/client_announcement_service.py). Manual only — never scheduled.
 */
export const CLIENT_NOTIFICATION_ACTION_TYPE = "common_client_notification";

// A function, not a constant: App.js imports this page, so `API` is not yet
// initialised while this file loads (circular import).
const base = () => `${API}/notification-config/admin/client-announcement`;
const POLL_MS = 2000;
const MAX_POLL_FAILURES = 5;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function CommonClientNotificationPanel({ user, configs = [], onConfigSaved }) {
  const canManage = can(user, "notifications:manage");
  const [setup, setSetup] = useState(null); // { types (each with its upload columns), limits }
  const [setupError, setSetupError] = useState(null);
  const [sender, setSender] = useState(undefined);
  const [type, setType] = useState(null);
  // Per announcement type: { message, upload }. A send only carries the selected type's draft.
  const [drafts, setDrafts] = useState({});
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progress, setProgress] = useState(null); // null = idle; { done, total } while sending
  const [outcome, setOutcome] = useState(null);
  const idem = useRef({ key: null, hash: null });
  const inFlight = useRef(false);
  const mounted = useRef(true);
  // Set on every mount: StrictMode mounts twice in dev, and a ref left false would
  // drop every response (and stop job polling) for the rest of the page's life.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const loadSetup = useCallback(async () => {
    setSetupError(null);
    try {
      const { data } = await axios.get(`${base()}/defaults`);
      setSetup(data);
      setDrafts((prev) => (Object.keys(prev).length ? prev
        : Object.fromEntries(data.types.map((t) => [t.value, { message: { ...t.defaults }, upload: null }]))));
      setType((prev) => prev || data.types[0].value);
    } catch (err) {
      setSetupError(apiErrorMessage(err, "Could not load the email template"));
    }
  }, []);

  useEffect(() => {
    loadSetup();
    axios.get(`${base()}/sender`).then((res) => setSender(res.data)).catch(() => setSender(null));
  }, [loadSetup]);

  // Saved defaults = this template's notification_configs doc (loaded by the page).
  const saved = configs.find((c) => c.action_type === CLIENT_NOTIFICATION_ACTION_TYPE);
  // Re-fill the lists only when the SAVED lists change, so a refetch elsewhere on the
  // page never wipes edits in progress (same rule as useTemplateRecipients).
  const savedKey = JSON.stringify(configPayload(listsFromSaved(saved)));
  useEffect(() => {
    const lists = listsFromSaved(JSON.parse(savedKey));
    setTo(lists.to); setCc(lists.cc); setBcc(lists.bcc);
  }, [savedKey]);

  const recipientsDirty = recipientsChanged({ to, cc, bcc }, saved);

  const saveRecipients = async () => {
    if (!to.length) { toast.error("Add at least one To recipient to save"); return; }
    setSavingRecipients(true);
    const body = configPayload({ to, cc, bcc });
    try {
      if (saved) {
        await axios.put(`${API}/notification-config/admin/${CLIENT_NOTIFICATION_ACTION_TYPE}`, body);
      } else {
        await axios.post(`${API}/notification-config/admin`, { action_type: CLIENT_NOTIFICATION_ACTION_TYPE, is_enabled: true, ...body });
      }
      toast.success("Recipients saved");
      await onConfigSaved?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to save recipients"));
    } finally {
      if (mounted.current) setSavingRecipients(false);
    }
  };

  const discardRecipients = () => {
    const lists = listsFromSaved(saved);
    setTo(lists.to); setCc(lists.cc); setBcc(lists.bcc);
  };

  const spec = setup?.types.find((t) => t.value === type);
  const draft = drafts[type];
  const message = draft?.message;
  const upload = draft?.upload;
  const locations = upload?.rows || [];
  const sending = progress !== null;

  const updateDraft = (patch) => setDrafts((prev) => ({ ...prev, [type]: { ...prev[type], ...patch } }));

  const preview = useAnnouncementPreview(message && {
    announcement_type: type, ...message, locations, greeting_name: to[0]?.name?.trim() || null,
  });

  const payload = (idempotencyKey) => sendPayload({ type, to, cc, bcc, message, locations, idempotencyKey });

  const requestSend = () => {
    setShowErrors(true);
    const blocker = !canManage ? "You need permission to manage notifications to send this email."
      : sendBlocker({ to, cc, bcc, message, limits: setup.limits, locationErrors: upload?.errors.length || 0 });
    if (blocker) { toast.error(blocker); return; }
    // Same content + recipients => same key, so a retried request cannot send twice.
    const hash = JSON.stringify(payload(""));
    if (idem.current.hash !== hash) idem.current = { key: newIdempotencyKey(), hash };
    setConfirmOpen(true);
  };

  /** Poll until the backend reports the job finished. null = status unknown. */
  const waitForJob = async (jobId) => {
    let failures = 0;
    while (mounted.current) {
      await sleep(POLL_MS);
      try {
        const { data: job } = await axios.get(`${base()}/jobs/${jobId}`);
        failures = 0;
        setProgress({ done: job.sent_count + job.failed_count, total: job.total_recipients });
        if (["COMPLETED", "FAILED"].includes(job.status) || job.stalled) return job;
      } catch {
        failures += 1;
        if (failures >= MAX_POLL_FAILURES) return null;
      }
    }
    return null;
  };

  const send = async () => {
    // A ref, not state: two clicks in the same tick both see the old `sending`.
    if (inFlight.current) return;
    inFlight.current = true;
    setConfirmOpen(false);
    setOutcome(null);
    setProgress({ done: 0, total: to.length });
    try {
      const { data } = await axios.post(`${base()}/send`, payload(idem.current.key));
      idem.current = { key: null, hash: null };
      if (data.duplicate) toast.info("This email was already queued — showing that send's result.");
      const job = await waitForJob(data.job_id);
      if (!mounted.current) return;
      if (!job) {
        setOutcome({ tone: "warn", text: "Could not confirm the result — check the Logs tab before sending again." });
        toast.error("Could not confirm the send result");
        return;
      }
      const failed = job.recipients.filter((r) => r.status === "failed");
      if (job.stalled) {
        setOutcome({ tone: "warn", text: `Sending stopped after ${job.sent_count} of ${job.total_recipients} — the server may have restarted. Check the Logs tab.` });
        toast.error("The send stopped before finishing");
      } else if (job.status === "FAILED") {
        setOutcome({ tone: "error", text: job.error_message || "The send failed." });
        toast.error(job.error_message || "The send failed");
      } else if (failed.length) {
        // Leave only the failed addresses in To, ready to retry.
        setTo((prev) => prev.filter((r) => failed.some((f) => f.email === r.email)));
        setOutcome({ tone: "error", text: `Sent to ${job.sent_count} of ${job.total_recipients}. Failed: ${failed.map((f) => `${f.email} (${f.error})`).join("; ")}. To now holds only the failed addresses.` });
        toast.warning(`Sent ${job.sent_count} of ${job.total_recipients} — ${failed.length} failed`);
      } else {
        setTo([]); // cleared so a second click cannot resend the same email by accident
        setShowErrors(false);
        setOutcome({ tone: "ok", text: `Sent to all ${job.sent_count} To recipient(s).` });
        toast.success(`Email sent to ${job.sent_count} recipient(s)`);
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, "The email could not be sent", { recipients: "To" }));
    } finally {
      inFlight.current = false;
      if (mounted.current) setProgress(null);
    }
  };

  if (!setup || !message) {
    return setupError ? (
      <Card><CardContent className="p-5 flex flex-wrap items-center gap-3 text-sm">
        <TriangleAlert className="w-5 h-5 text-red-500" /><span className="flex-1">{setupError}</span>
        <Button type="button" variant="outline" size="sm" onClick={loadSetup}>Retry</Button>
      </CardContent></Card>
    ) : (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6" aria-busy="true">
        {[0, 1].map((i) => <div key={i} className="h-72 rounded-xl bg-slate-100 animate-pulse" />)}
      </div>
    );
  }

  const outcomeClass = { ok: "text-green-700", warn: "text-amber-700", error: "text-red-600" };
  const locationNote = locations.length ? `, listing ${locations.length} affected location(s)` : "";

  return (
    <div className="space-y-6" data-testid="client-notification-panel">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-teal-600 shrink-0" />Common Client Notification
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Send an operational announcement to clients — embargoes, festival delays, service changes or revised
          commercial terms. The email preview updates as you type.
        </p>
      </div>

      {/* Picks only the default wording */}
      <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
        <Label htmlFor="ca-type" className="text-xs font-medium text-gray-500 uppercase tracking-wider shrink-0">
          Announcement type
        </Label>
        <Select value={type} onValueChange={(v) => { setType(v); setShowErrors(false); }} disabled={sending}>
          <SelectTrigger id="ca-type" className="w-full sm:w-80 bg-white" data-testid="ca-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {setup.types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <div className="space-y-6 min-w-0">
          <RecipientFields to={to} setTo={setTo} cc={cc} setCc={setCc} bcc={bcc} setBcc={setBcc}
            limits={setup.limits} disabled={!canManage || sending}
            save={canManage ? {
              dirty: recipientsDirty, saving: savingRecipients, hasSaved: Boolean(saved),
              onSave: saveRecipients, onDiscard: discardRecipients,
            } : null} />
          <MessageEditor message={message} setMessage={(m) => updateDraft({ message: m })} defaults={spec.defaults}
            showErrors={showErrors} disabled={!canManage || sending} />
          <LocationsUpload type={type} columns={spec.columns} limits={setup.limits}
            upload={upload} setUpload={(u) => updateDraft({ upload: u })} disabled={!canManage || sending} />
        </div>

        {/* Preview — sticky beside the form on wide screens, below it otherwise */}
        <Card className="min-w-0 xl:sticky xl:top-4 xl:max-h-[calc(100dvh-2rem)] xl:overflow-y-auto">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-lg flex items-center gap-2">
                Email Preview{preview.loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" aria-label="Updating preview" />}
              </CardTitle>
              <Button type="button" className="bg-teal-600 hover:bg-teal-700"
                onClick={requestSend} disabled={sending} data-testid="ca-trigger-now">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                {sending ? `Sending ${progress.done}/${progress.total}…` : "Trigger Now"}
              </Button>
            </div>
            {!canManage && <p className="text-xs text-amber-700">You need permission to manage notifications to send this email.</p>}
            {outcome && <p className={`text-xs ${outcomeClass[outcome.tone]}`} role="status" data-testid="ca-outcome">{outcome.text}</p>}
          </CardHeader>
          <CardContent className="text-sm">
            <EmailPreviewHeader sender={sender} to={to.map((r) => r.email)} cc={cc.map((r) => r.email)}
              bcc={bcc.map((r) => r.email)} subject={preview.data?.subject || message.subject || "—"}
              subjectTestId="ca-preview-subject" />
            {to.length > 1 && (
              <p className="text-xs text-gray-500 pt-2">
                Each To address receives its own email ({to.length} emails); previewing as {to[0].email}.
              </p>
            )}
            <div className="pt-3">
              {preview.error && <p className="text-sm text-red-600" role="alert">{preview.error}</p>}
              {preview.data ? <EmailPreviewFrame html={preview.data.html} />
                : !preview.error && <div className="h-60 rounded-md bg-slate-100 animate-pulse" aria-busy="true" />}
            </div>
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Send ${spec.label} notification?`}
        description={`"${message.subject.trim()}" will be sent as ${to.length} separate email(s) — one per To recipient — with ${cc.length} CC and ${bcc.length} BCC address(es) copied on each${locationNote}. This sends real emails and cannot be undone.`}
        confirmLabel="Send Email"
        onConfirm={send}
      />
    </div>
  );
}
